// Role-based permission definitions for INTERNAL USERS ONLY
// These roles are for employees and investors, not business clients
export const ROLES = {
  OWNER: 'owner',           // Organization owner — full access
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',           // Full system administrator
  EMPLOYEE: 'employee',     // Regular employee with operational access
  MANAGER: 'manager',       // Manager with broader access
  INVESTOR: 'investor'      // Read-only access with sensitive data hidden
}

// Admin defaults to the full catalog (role_permissions). Owner/super_admin
// are locked — the GUI cannot restrict them.
export const FULL_ACCESS_ROLES = [ROLES.OWNER, ROLES.ADMIN, ROLES.SUPER_ADMIN]
export const LOCKED_ROLES = [ROLES.OWNER, ROLES.SUPER_ADMIN]

export function isFullAccessRole(role) {
  return FULL_ACCESS_ROLES.includes(role)
}

export function isLockedRole(role) {
  return LOCKED_ROLES.includes(role)
}

// Permission definitions
export const PERMISSIONS = {
  // User Management
  MANAGE_USERS: 'manage_users',
  VIEW_USERS: 'view_users',
  
  // Client Management
  MANAGE_CLIENTS: 'manage_clients',
  VIEW_CLIENTS: 'view_clients',
  
  // Financial
  VIEW_COSTS: 'view_costs',
  MANAGE_PRICING: 'manage_pricing',
  VIEW_PROFITS: 'view_profits',
  
  // Orders
  CREATE_PURCHASE_ORDERS: 'create_purchase_orders',
  APPROVE_PURCHASE_ORDERS: 'approve_purchase_orders',
  VIEW_PURCHASE_ORDERS: 'view_purchase_orders',
  MANAGE_SALES_ORDERS: 'manage_sales_orders',
  VIEW_SALES_ORDERS: 'view_sales_orders',
  
  // Vendors
  MANAGE_VENDORS: 'manage_vendors',
  VIEW_VENDORS: 'view_vendors',
  
  // Inventory
  MANAGE_INVENTORY: 'manage_inventory',
  VIEW_INVENTORY: 'view_inventory',
  
  // Production
  MANAGE_RECIPES: 'manage_recipes',
  VIEW_RECIPES: 'view_recipes',
  MANAGE_BATCHES: 'manage_batches',
  VIEW_BATCHES: 'view_batches',
  
  // Analytics
  VIEW_ANALYTICS: 'view_analytics',
  VIEW_PROJECTIONS: 'view_projections'
}

// Default permissions by role
export const ROLE_PERMISSIONS = {
  [ROLES.ADMIN]: Object.values(PERMISSIONS), // Full system access
  
  [ROLES.MANAGER]: [
    PERMISSIONS.VIEW_USERS,
    PERMISSIONS.MANAGE_CLIENTS,
    PERMISSIONS.VIEW_CLIENTS,
    PERMISSIONS.VIEW_COSTS,
    PERMISSIONS.MANAGE_PRICING,
    PERMISSIONS.VIEW_PROFITS,
    PERMISSIONS.CREATE_PURCHASE_ORDERS,
    PERMISSIONS.APPROVE_PURCHASE_ORDERS,
    PERMISSIONS.VIEW_PURCHASE_ORDERS,
    PERMISSIONS.MANAGE_SALES_ORDERS,
    PERMISSIONS.VIEW_SALES_ORDERS,
    PERMISSIONS.MANAGE_VENDORS,
    PERMISSIONS.VIEW_VENDORS,
    PERMISSIONS.MANAGE_INVENTORY,
    PERMISSIONS.VIEW_INVENTORY,
    PERMISSIONS.MANAGE_RECIPES,
    PERMISSIONS.VIEW_RECIPES,
    PERMISSIONS.MANAGE_BATCHES,
    PERMISSIONS.VIEW_BATCHES,
    PERMISSIONS.VIEW_ANALYTICS,
    PERMISSIONS.VIEW_PROJECTIONS
  ],
  
  [ROLES.EMPLOYEE]: [
    PERMISSIONS.VIEW_CLIENTS,
    PERMISSIONS.CREATE_PURCHASE_ORDERS,
    PERMISSIONS.VIEW_PURCHASE_ORDERS,
    PERMISSIONS.VIEW_SALES_ORDERS,
    PERMISSIONS.VIEW_VENDORS,
    PERMISSIONS.MANAGE_INVENTORY,
    PERMISSIONS.VIEW_INVENTORY,
    PERMISSIONS.MANAGE_RECIPES,
    PERMISSIONS.VIEW_RECIPES,
    PERMISSIONS.MANAGE_BATCHES,
    PERMISSIONS.VIEW_BATCHES,
    PERMISSIONS.VIEW_ANALYTICS
  ],
  
  [ROLES.INVESTOR]: [
    // Read-only access to non-sensitive data
    PERMISSIONS.VIEW_INVENTORY,
    PERMISSIONS.VIEW_SALES_ORDERS,
    PERMISSIONS.VIEW_ANALYTICS,
    PERMISSIONS.VIEW_PROJECTIONS
    // Note: Costs and vendor pricing are NOT included for investors
  ]
}

// Route permission requirements
export const ROUTE_PERMISSIONS = {
  '/admin/users': [PERMISSIONS.VIEW_USERS],
  '/clients': [PERMISSIONS.VIEW_CLIENTS],
  '/vendors': [PERMISSIONS.VIEW_VENDORS],
  '/vendor-pricing': [PERMISSIONS.MANAGE_PRICING],
  '/purchase-orders': [PERMISSIONS.VIEW_PURCHASE_ORDERS],
  '/sales-orders': [PERMISSIONS.VIEW_SALES_ORDERS],
  '/costs': [PERMISSIONS.VIEW_COSTS],
  '/profit-projections': [PERMISSIONS.VIEW_PROFITS]
}

// Helper functions
export function hasPermission(user, permission) {
  if (!user) return false
  
  // Check pageAccess overrides first (highest priority)
  if (user.pageAccess && typeof user.pageAccess[permission] === 'boolean') {
    return user.pageAccess[permission]
  }
  
  // Check direct permissions
  if (user.permissions && user.permissions[permission]) {
    return true
  }
  
  // Check role-based permissions
  const rolePermissions = ROLE_PERMISSIONS[user.role] || []
  return rolePermissions.includes(permission)
}

export function hasAnyPermission(user, permissions) {
  return permissions.some(permission => hasPermission(user, permission))
}

export function hasAllPermissions(user, permissions) {
  return permissions.every(permission => hasPermission(user, permission))
}

export function canAccessRoute(user, route) {
  const requiredPermissions = ROUTE_PERMISSIONS[route]
  if (!requiredPermissions) return true // No permissions required
  
  return hasAnyPermission(user, requiredPermissions)
}

export function getUserPermissions(user) {
  if (!user) return []
  
  // Get role-based permissions
  const rolePermissions = ROLE_PERMISSIONS[user.role] || []
  
  // Merge with direct permissions
  const directPermissions = Object.keys(user.permissions || {})
    .filter(key => user.permissions[key])
  
  // Return unique permissions
  return [...new Set([...rolePermissions, ...directPermissions])]
}